import { AgGridReact } from "ag-grid-react";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { PageableTable } from "../../../../components/table";
import { usePageableTable } from "../../../../context/PageableTableContext";
import {
  retrieveValueFromLocalStorageObject,
  saveObjectsToLocalStorage,
} from "../../../../helpers/local-storage.helper";
import { Predicates } from "../../../../libraries/predicates/predicates";
import { PageableTableActions } from "../../../../states/pageable-table";
import { TablePagePersistentTypes } from "../../../../types/persistent-page-filters";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import { formatGlobalProductIntoOption } from "../../../../helpers/global-product.helper";
import { ItemNumberSearchActionLinks } from "../links";
import { LIFECYCLE_STATUS_LABEL } from "constants/labels";
import useItemNumberSearch from "../../../../hooks/useItemNumberSearch";
import { Link } from "react-router-dom";
import { formatMarketingMaterialIntoOption } from "../../../../helpers/marketing-material.helper";


type SearchTableProp = {
  localStoragePrefix: TablePagePersistentTypes["prefix"];
  checkUnsaved?: boolean;
  showCustomFields?: Array<string>;
  filters?: {
    globalProductId?: number;
    tccId?: number;
    queryString?: string;
  };
  enableAdvancedFilters?: boolean;
  showClearButton?: boolean;
};

const SearchTable = memo(
  ({
    localStoragePrefix,
    filters,
    showCustomFields,
    enableAdvancedFilters = true,
    showClearButton = true,
  }: SearchTableProp) => {
    const [pageSize, setPageSize] = useState(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "pageSize",
        50,
      );
    });
    const gridRef = useRef<AgGridReact>(null);
    const [search, setSearch] = useState(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "search",
        "",
      );
    });
    const [searchToBeSent, setSearchToBeSent] = useState<string>(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "search",
        "",
      );
    });

    const { debounce } = useContext<DebounceContextType>(DebounceContext);

    const {
      state: { offset, isFirstRender },
      dispatch,
    } = usePageableTable();

    const defaultColDef = useMemo(
      () => ({
        sortingOrder: ["asc" as const, "desc" as const],
        minWidth: 100,
        flex: 1,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        wrapText: true,
        autoHeight: true,
        suppressMenu: true,
        suppressMovable: true,
        icons: {
          sortAscending: "<i class='fa fa-sort-up'/>",
          sortDescending: "<i class='fa fa-sort-down'/>",
          sortUnSort: "<i class='fa fa-sort' style='color:#e3e6f0'></i>",
          filter: "<i class='fa fa-filter'></i>",
        },
      }),
      [],
    );

    const columnDefs = useMemo(
      () => [
        {
          colId: "itemNumber",
          headerName: "Item Number",
          field: "itn_itemno",
          // valueGetter: (params: any) => {
          //   return params.data.itn_itemno + " - " + params.data.itn_itemnm;
          // },
          minWidth: 225,
          flex: 3,
          cellRenderer: (params: any) => {
            return (
              <Link
                to={`/itemnumber/view?id=${params.data.id}`}
                className="fw-bold"
              >
                {!!params.data.itn_itemno
                  ? params.data.itn_itemno + " - " + params.data.name
                  :formatGlobalProductIntoOption(params.data.global_product)?.label.split(" -")[0] +
                    "-"+params.data.suffix +
                    " - " +
                    params.data.name
                }
              </Link>
            );
          },
        },
        {
          colId: "global_product",
          headerName: "Global Product",
          field: "global_product",
          valueGetter: (params: any) => {
            return formatGlobalProductIntoOption(params.data.global_product)
              ?.label;
          },
          minWidth: 225,
          flex: 3,
        },
        {
          colId: "marketing_material",
          headerName: "Material (Marketing)",
          field: "itn_reqmkt",
          minWidth: 120,
          maxWidth: 150,
          valueGetter: (params: any) => {
            return params.data?.marketing_material === null ||
              params.data?.marketing_material === ""
              ? ""
              : formatMarketingMaterialIntoOption(params.data.marketing_material)?.label
;
          },
        },
        {
          colId: "itn_lifsts",
          field: "itn_lifsts",
          headerName: `${LIFECYCLE_STATUS_LABEL}`,
          valueGetter: (params: any) => {

            return params.data.line_planning_status;
          },
          minWidth: 120,
          maxWidth: 150,
          // cellRenderer: LinePlanningStatusCellRenderer,
          // tooltipComponent: LinePlanningStatusBreakdownTooltip,
          // tooltipValueGetter: (p: ITooltipParams) => p.value,
        },
        {
          field: "select",
          headerName: "Actions",
          minWidth: 120,
          maxWidth: 150,
          cellRenderer: ItemNumberSearchActionLinks,
          cellStyle: { whiteSpace: "normal" },
        },
      ],
      [],
    );

    const { data: itemnumbers, isLoading: isLoading } = useItemNumberSearch({
      limit: pageSize,
      offset,
      queryString: filters?.queryString,
      search: Predicates.isNotNullAndNotUndefinedAndNotEmpty(showCustomFields)
        ? searchToBeSent
        : undefined,
    });

    const tableProp = useMemo(
      () => ({
        gridRef,
        rowData: itemnumbers?.data ?? [],
        columnDefs,
        defaultColDef,
      }),
      [gridRef, itemnumbers],
    );

    const onPageSizeChanged = useCallback((e: any) => {
      setPageSize(Number(e.target.value));
    }, []);

    const handleTableSearch = (elem: any) => {
      setSearch(elem.target.value.toLowerCase().trimStart());
      debounce(setSearchToBeSent, 500)(elem?.target?.value ?? "");
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableTopFilters: {
          search: elem.target.value.toLowerCase().trimStart(),
          pageSize: pageSize,
        },
      });
    };

    const clearAllFilters = () => {
      setSearch("");
      setSearchToBeSent("");
      if (Predicates.isNotNullAndNotUndefined(gridRef.current)) {
        gridRef.current.api?.setFilterModel(null);
      }
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableFilters: [],
        lastTableTopFilters: {},
      });
    };

    useEffect(() => {
      dispatch(PageableTableActions.setOffset(0));
    }, [filters?.queryString, dispatch]);

    useEffect(() => {
      dispatch(PageableTableActions.setPageSize(pageSize));
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableTopFilters: {
          search: search,
          pageSize: Number(pageSize),
        },
      });
    }, [pageSize, dispatch]);

    useEffect(() => {
      if (!isLoading && Predicates.isNotNullAndNotUndefined(itemnumbers))
        dispatch(
          PageableTableActions.setTotal(itemnumbers.data.length > 0 ? itemnumbers?.total : 0),
        );
    }, [isLoading, itemnumbers, dispatch]);

    useEffect(() => {
      dispatch(PageableTableActions.setLoading(isLoading));
    }, [isLoading, dispatch]);

    useEffect(() => {
      if (
        Predicates.isNotNullAndNotUndefinedAndNotEmpty(filters?.queryString) &&
        isFirstRender
      ) {
        dispatch(PageableTableActions.setFirstRender(false));
      }
    }, [filters, isFirstRender, dispatch]);

    return (
      <>
        {enableAdvancedFilters ? (
          <div className="d-flex justify-content-between table-top-container">
            <div className="d-flex align-items-center">
              <label className="d-inline-block">
                Show
                <select
                  onChange={onPageSizeChanged}
                  className="table-top-input"
                  id="page-size"
                  value={pageSize}
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">All</option>
                </select>
                entries
              </label>
            </div>

            {/*FIXME: Disabled because it is not working, need to fix to enable again, probably after the deploy*/}
            {/*<div className="d-flex justify-content-center align-items-center">*/}
            {/*  <div*/}
            {/*    id="pp_search_filter"*/}
            {/*    className="dataTables_filter table-top-search"*/}
            {/*  >*/}
            {/*    <label>*/}
            {/*      Search:*/}
            {/*      <input*/}
            {/*        onChange={handleTableSearch}*/}
            {/*        type="search"*/}
            {/*        className="table-top-input"*/}
            {/*        placeholder=""*/}
            {/*        aria-controls="pp_search"*/}
            {/*        value={search}*/}
            {/*      />*/}
            {/*    </label>*/}
            {/*  </div>*/}

            {/*  {showClearButton ? (*/}
            {/*    <button*/}
            {/*      className="icon-button ml-3 mr-3"*/}
            {/*      onClick={clearAllFilters}*/}
            {/*      disabled={Predicates.isNullOrUndefinedOrEmpty(search)}*/}
            {/*    >*/}
            {/*      <FontAwesomeIcon size="lg" icon={faFilterCircleXmark} />*/}
            {/*    </button>*/}
            {/*  ) : (*/}
            {/*    <></>*/}
            {/*  )}*/}
            {/*</div>*/}
          </div>
        ) : (
          <></>
        )}

        <PageableTable tableProp={tableProp} />
      </>
    );
  },
);

export default SearchTable;
